import {
  StateMachineResponse,
  LoginWithIdpConnectionRequest,
  TenantType,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { loginWithIdpConnection } from '@wix/ambassador-iam-authentication-v1-authentication/http';
import { WIX_TENANT_ID } from './constants';

export interface OpenIdpPopupOptions {
  idpConnectionId: string;
  ldSessionId: string;
  extraCustomPayload?: Record<string, string>;
  /**
   * Called once the popup window exists and has been pointed at the IdP login
   * URL. Not called when the browser blocked the popup or key setup failed.
   */
  onPopupOpened?: () => void;
}

/**
 * Opens a popup window to initiate IDP (Identity Provider) login flow.
 * Uses BroadcastChannel and AES-GCM encryption for secure communication
 * between the popup and the parent window.
 *
 * @param options - Configuration options for the IDP popup
 * @returns Promise resolving to the IAM platform login response
 */
export const openIdpPopup = async (
  options: OpenIdpPopupOptions,
): Promise<StateMachineResponse> => {
  const { idpConnectionId, ldSessionId, extraCustomPayload, onPopupOpened } =
    options;

  // Build request using Ambassador types for type-safety
  const request: Omit<
    LoginWithIdpConnectionRequest,
    'sessionId' | 'encryptionKey'
  > = {
    idpConnectionId,
    tenantId: WIX_TENANT_ID,
    tenantType: TenantType.ACCOUNT,
    customPayload: {
      ...extraCustomPayload,
      ldSessionId,
    },
  };

  return openPopupWithRequest(request, onPopupOpened);
};

const openPopupWithRequest = async (
  request: Omit<LoginWithIdpConnectionRequest, 'sessionId' | 'encryptionKey'>,
  onPopupOpened?: () => void,
): Promise<StateMachineResponse> => {
  // Open synchronously before any awaits — iOS Safari blocks window.open()
  // calls made after an await because the user-gesture context is lost.
  const popup = window.open('about:blank', 'oauthPopup', buildPopupFeatures());

  if (!popup) {
    throw new Error('Popup was blocked');
  }

  const sessionId = window.crypto.randomUUID();
  const key = await window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
  const encryptionKey = uint8ToBase64(
    new Uint8Array(await window.crypto.subtle.exportKey('raw', key)),
  );

  const channelName = `wix-idp-${sessionId}`;
  const url = buildLoginUrl({ ...request, sessionId, encryptionKey });

  return new Promise((resolve, reject) => {
    const bc = new BroadcastChannel(channelName);

    bc.addEventListener('message', async (event) => {
      const { data: eventData } = event;
      const { iv, data } = eventData;

      try {
        const decryptedData = await window.crypto.subtle.decrypt(
          { name: 'AES-GCM', iv },
          key,
          data,
        );
        const loginResponse = JSON.parse(
          new TextDecoder().decode(decryptedData),
        );

        if (loginResponse.error) {
          reject({
            error: loginResponse.error,
            redirectUrl: loginResponse.redirectUrl,
            // errorData carries context such as the account email (used to
            // resolve SSO / account verification) and an optional redirectUrl.
            errorData: loginResponse.errorData,
          });
        } else {
          resolve(JSON.parse(loginResponse.response));
        }
      } catch (err) {
        reject(err);
      }
      try {
        bc.postMessage(await encryptedCloseMessage(key));
      } catch {}
      bc.close();
    });

    popup.location.href = url;
    onPopupOpened?.();
  });
};

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

/**
 * Builds the window.open feature string for a popup centered on the screen.
 * Uses the multi-monitor-aware screen coordinates (screenLeft/screenTop and
 * outer dimensions) so the popup is centered relative to the window the user
 * is actually looking at, falling back to the primary screen.
 */
const buildPopupFeatures = (): string => {
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;

  const width =
    window.innerWidth ?? document.documentElement.clientWidth ?? screen.width;
  const height =
    window.innerHeight ??
    document.documentElement.clientHeight ??
    screen.height;

  const left = dualScreenLeft + (width - POPUP_WIDTH) / 2;
  const top = dualScreenTop + (height - POPUP_HEIGHT) / 2;

  return `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`;
};

const uint8ToBase64 = (arr: Uint8Array): string =>
  btoa(
    Array(arr.length)
      .fill('')
      .map((_, i) => String.fromCharCode(arr[i]))
      .join(''),
  );

const encryptedCloseMessage = async (key: CryptoKey) => {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const data = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode('close'),
  );
  return { iv, data };
};

const buildLoginUrl = (request: LoginWithIdpConnectionRequest): string => {
  // Use Ambassador to generate the request options
  const requestOptionsFactory = loginWithIdpConnection(request);
  const requestOptions = requestOptionsFactory({
    isSSR: false,
    host: window.location.host,
  });

  // Build the full URL from the request options
  const { url, params } = requestOptions;
  if (!url) {
    throw new Error('Failed to build IDP login URL');
  }
  const queryString = params?.toString() || '';
  return queryString ? `${url}?${queryString}` : url;
};
